/* variables */
:root {
  --xendit-font-family: sans-serif;
  --xendit-color-primary: #1762ee;
  --xendit-color-text: #252525;
  --xendit-color-text-secondary: #585858;
  --xendit-color-text-placeholder: #7d7d7d;
  --xendit-color-disabled: #f7f7f7;
  --xendit-color-danger: #d1414d;
  --xendit-color-border: #f3f3f3;
  --xendit-color-background: #ffffff;
  --xendit-focus-shadow: 0px 0px 0px 2px
    color-mix(in srgb, var(--xendit-color-primary) 15%, transparent);
  --xendit-card-shadow: 0px 4px 8px 0px #25252514;
  --xendit-dialog-shadow: 8px 8px 12px 8px #25252529;
  --xendit-animation-duration: 0.3s;
  --xendit-animation-ease: ease-in-out;
  --xendit-radius-1: 8px;
  --xendit-z-index-focus: 2;
  --xendit-z-index-overlay: 3;

  /* Browser-specific border collapse margins */
  --xendit-border-collapse-offset: -0.5px; /* Default for Chrome/Edge */
  --xendit-border-collapse-webkit-gecko: -1.5px; /* Fix Safari and Firefox subpixel rendering */

  /* QR code colors */
  --xendit-qr-foreground-color: #000000;
  --xendit-qr-background-color: #ffffff;

  /* icon colors */
  --xendit-color-icon-primary: #7c7c7c;
  --xendit-color-icon-secondary: #f1f1f1;
}

/* Set default font for root components */
xendit-channel-picker,
xendit-action-container,
xendit-payment-channel {
  display: block;
  unicode-bidi: isolate;
  font-family: var(--xendit-font-family);
  color: var(--xendit-color-text);
  touch-action: manipulation;
  transition: filter var(--xendit-animation-duration)
    var(--xendit-animation-ease);
}

.xendit-default-action-container {
  font-family: var(--xendit-font-family);
  touch-action: manipulation;
}

xendit-action-container {
  position: relative;
  flex: 1; /* Action container usually wants to stretch to fit, assuming the parent is a flexbox. */
  touch-action: manipulation;
}

xendit-payment-channel button,
xendit-payment-channel textarea,
xendit-payment-channel select,
xendit-payment-channel input,
xendit-payment-channel input::placeholder {
  font-family: inherit;
}

xendit-payment-channel input::placeholder,
xendit-payment-channel textarea::placeholder,
xendit-payment-channel select::placeholder {
  color: var(--xendit-color-text-placeholder);
}

/* Reusable typography classes */
.xendit-text-12 {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.01px;
}

.xendit-text-14 {
  font-size: 14px;
  line-height: 16px;
  letter-spacing: -0.09px;
}

.xendit-text-16 {
  font-size: 16px;
  line-height: 16px;
  letter-spacing: -0.09px;
}

.xendit-text-18 {
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.26px;
}

.xendit-text-20 {
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.33px;
}

.xendit-text-12,
.xendit-text-14,
.xendit-text-16 {
  font-weight: 400;
}

.xendit-text-semibold {
  font-weight: 600;
}

.xendit-text-bold {
  font-weight: 700;
}

.xendit-text-link {
  color: var(--xendit-color-primary);
}
.xendit-text-secondary {
  color: var(--xendit-color-text-secondary);
}

.xendit-text-center {
  text-align: center;
}

.xendit-dotted-line {
  height: 1px;
  border: none;
  background: repeating-linear-gradient(
    to right,
    var(--xendit-color-border) 0px 4px,
    transparent 4px 8px
  );
  margin: 0;
}

/* Accordion component */

.xendit-accordion {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  background: var(--xendit-color-background);
}

.xendit-accordion-item:not(:first-child) {
  border-top: 1px solid var(--xendit-color-border);
  border-top-left-radius: var(--xendit-radius-1);
  border-top-right-radius: var(--xendit-radius-1);
}

.xendit-accordion-item-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-gap: 16px;
  align-items: center;
  cursor: pointer;
  padding: 24px 16px;
  color: var(--xendit-color-text);
  border-radius: 8px;
}

.xendit-accordion-item-disabled .xendit-accordion-item-header {
  opacity: 0.5;
  cursor: not-allowed;
}

.xendit-accordion-item-header-title {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.xendit-accordion-item-disabled .xendit-accordion-item-header-title,
.xendit-accordion-item-disabled .xendit-accordion-item-header-icon {
  opacity: 0.5;
}

.xendit-accordion-item.xendit-accordion-item-open
  .xendit-accordion-item-header {
  color: var(--xendit-color-primary);
}

.xendit-accordion-item-chevron {
  color: var(--xendit-color-text);
}

.xendit-accordion-item-disabled .xendit-accordion-item-chevron {
  display: none;
}

.xendit-accordion-item-chevron g {
  transition: transform var(--xendit-animation-duration)
    var(--xendit-animation-ease);
}

.xendit-accordion-item-header:focus-visible {
  border-radius: 8px;
  box-shadow: var(--xendit-focus-shadow);
  outline: none;
}

.xendit-accordion-item-content {
  overflow: hidden;
  transition: height var(--xendit-animation-duration)
    var(--xendit-animation-ease);
}

.xendit-accordion-item.xendit-accordion-item-closed
  .xendit-accordion-item-content {
  height: 0;
}

.xendit-accordion-item.xendit-accordion-item-open
  .xendit-accordion-item-content {
  height: calc-size(auto, size);
}

.xendit-accordion-item-padding {
  padding: 16px;
}

/* Channel picker digital wallet section */

.xendit-channel-picker-digital-wallet-section {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

/* Channel picker group */

.xendit-channel-picker-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.xendit-channel-picker-group select {
  border: 1px solid var(--xendit-color-border);
  border-radius: 8px;
  padding: 12px;
  appearance: none;
  outline: none;
}

.xendit-channel-picker-group select:focus {
  box-shadow: var(--xendit-focus-shadow);
}

.xendit-channel-logo {
  width: 24px;
  height: 16px;
  margin: -5px 0;
  padding: 4px;
  border: 1px solid var(--xendit-color-border);
  border-radius: 4px;
  background-color: white; /* always white, not --xendit-color-background */
  object-fit: contain;
}

/* Payment channel */

xendit-payment-channel[inert],
xendit-channel-picker[inert] {
  filter: opacity(0.5) grayscale(1);
}

.xendit-payment-channel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.xendit-payment-channel-banner {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.xendit-payment-channel-instructions {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 12px;
  color: var(--xendit-color-text-secondary);
}

.xendit-payment-channel-instructions-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.xendit-payment-channel-instructions-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.xendit-payment-channel-instructions-text p {
  margin: 0;
}

/* Forms */

.xendit-channel-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.xendit-channel-form > form {
  display: contents;
}

.xendit-form-field-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.xendit-channel-form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.xendit-channel-form-field-group {
  display: grid;
  gap: 8px;
}

.xendit-channel-form-field-group-label-container {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.xendit-channel-form-field-group label {
  width: fit-content;
  cursor: pointer;
}

.xendit-form-field-group.invalid
  .xendit-channel-form-field
  .xendit-form-field-inner {
  border: 1px solid var(--xendit-color-danger);
}

.xendit-channel-form-field .xendit-form-field-inner {
  border: 1px solid var(--xendit-color-border);
  background: var(--xendit-color-background);
  border-radius: 8px;
  appearance: none;
}

.xendit-channel-form-field.field-radius-tl-0 .xendit-form-field-inner {
  border-top-left-radius: 0;
}

.xendit-channel-form-field.field-radius-tr-0 .xendit-form-field-inner {
  border-top-right-radius: 0;
}

.xendit-channel-form-field.field-radius-bl-0 .xendit-form-field-inner {
  border-bottom-left-radius: 0;
}

.xendit-channel-form-field.field-radius-br-0 .xendit-form-field-inner {
  border-bottom-right-radius: 0;
}

.xendit-channel-form-field.field-collapse-l,
.xendit-channel-form-field.field-collapse-l {
  margin-left: var(--xendit-border-collapse-offset);
}

.xendit-channel-form-field.field-collapse-r,
.xendit-channel-form-field.field-collapse-r {
  margin-right: var(--xendit-border-collapse-offset);
}

.xendit-channel-form-field.field-collapse-l .xendit-iframe-container {
  margin-left: calc(var(--xendit-border-collapse-offset) * 2);
  width: calc(100% + abs(var(--xendit-border-collapse-offset) * 2));
}

.xendit-channel-form-field.field-collapse-r .xendit-iframe-container {
  margin-right: 0;
}

.xendit-channel-form-field.field-collapse-t .xendit-form-field-inner {
  margin-top: var(--xendit-border-collapse-offset);
}

.xendit-channel-form-field.field-collapse-b .xendit-form-field-inner {
  margin-bottom: var(--xendit-border-collapse-offset);
}

/* Safari and Firefox specific adjustments */
@supports (font: -apple-system-body) or (-moz-appearance: none) {
  .xendit-channel-form-field.field-collapse-l .xendit-iframe-container {
    margin-left: 0;
    width: 100%;
  }

  .xendit-channel-form-field.field-collapse-b input,
  .xendit-channel-form-field.field-collapse-b button {
    margin-bottom: var(--xendit-border-collapse-webkit-gecko);
    height: calc(100% + abs(var(--xendit-border-collapse-webkit-gecko) * 2));
  }
}

.xendit-channel-form-field.field-collapse-r .xendit-iframe-container:focus {
  border-right: none;
}

.xendit-channel-form-field.field-collapse-l .xendit-iframe-container:focus {
  border-left: none;
}

.xendit-card-brand-logo {
  width: 24px;
  height: 16px;
  border: 1px solid var(--xendit-color-border);
  border-radius: 4px;
  padding: 4px;
  background-color: white; /* always white, not --xendit-color-background */
}

.xendit-card-brands-list {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 4px;
}

.xendit-channel-form-field .xendit-form-field-inner:focus,
.xendit-channel-form-field .xendit-form-field-inner.xendit-field-focus {
  border-color: var(--xendit-color-primary);
  box-shadow: var(--xendit-focus-shadow);
  outline: none;
  z-index: var(--xendit-z-index-focus);
  position: relative;
}

.xendit-channel-form-field button.xendit-dropdown-open {
  z-index: var(--xendit-z-index-focus);
}

.xendit-channel-form-field input.xendit-form-field-inner {
  padding: 12px;
}

.xendit-channel-form-field .xendit-iframe-container {
  display: flex;
  height: 40px;
  width: 100%;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.xendit-channel-form-field .xendit-iframe-container iframe {
  height: 40px;
  width: 100%;
  border: none;
  min-width: 0;
}

.xendit-channel-form-field.xendit-form-field-span-1 {
  grid-column: span 1;
}

.xendit-channel-form-field.xendit-form-field-span-2 {
  grid-column: span 2;
}

.xendit-checkbox {
  display: flex;
  align-items: center;
}

.xendit-checkbox-box {
  display: flex;
}

.xendit-checkbox input {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}

.xendit-checkbox label {
  cursor: pointer;
  padding-left: 8px;
}

.xendit-checkbox input {
  opacity: 0;
  position: absolute;
}

.xendit-checkbox input + .xendit-checkbox-graphic {
  width: 16px;
  height: 16px;
  border: 1px solid var(--xendit-color-border);
  border-radius: 4px;
  box-sizing: border-box;
  color: transparent;
}

.xendit-checkbox input:checked + .xendit-checkbox-graphic {
  color: var(--xendit-color-background);
  background-color: var(--xendit-color-primary);
  border-color: var(--xendit-color-primary);
}

.xendit-checkbox input:focus + .xendit-checkbox-graphic {
  border: 1px solid var(--xendit-color-primary);
  box-shadow: var(--xendit-focus-shadow);
}

/* Dialog */

.xendit-dialog-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  padding: 48px;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
}

.xendit-dialog {
  position: relative;
}

.xendit-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--xendit-color-text);
  padding: 16px;
  gap: 16px;
  line-height: 20px;
  border-bottom: 1px solid var(--xendit-color-border);
}

.xendit-dialog-header button {
  display: flex;
  background: none;
  border: none;
  color: var(--xendit-color-text);
  cursor: pointer;
  padding: 0;
}

.xendit-dialog-floating-close {
  display: none;
}

.xendit-dialog .xendit-dialog-floating-close {
  display: flex;
}

.xendit-dialog-floating-close {
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  color: var(--xendit-color-text);
  cursor: pointer;
  padding: 0;
}

.xendit-dialog-body {
  display: flex;
  padding: 24px;
  background-color: var(--xendit-color-background);
  border-radius: 8px;
  box-shadow: var(--xendit-dialog-shadow);
}

.xendit-error-message {
  color: var(--xendit-color-danger);
}

/* Phone input Dropdown */
.xendit-input-phone {
  display: flex;
  gap: 8px;
}

.xendit-phone-number-input {
  width: 100%;
}

/* Phone input Dropdown ends */

/* Dropdown */
.xendit-dropdown {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  text-align: left;
  background-color: var(--xendit-color-background);
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  justify-content: space-between;
  cursor: pointer;
  padding: 0;
  outline: none;
  overflow: hidden;
}

.xendit-dropdown:focus {
  border-color: var(--xendit-color-primary);
  box-shadow: var(--xendit-focus-shadow);
  outline: none;
  z-index: var(--xendit-z-index-focus);
  position: relative;
}

.xendit-dropdown.xendit-dropdown-has-asset {
  grid-template-columns: auto 1fr auto;
  padding-left: 12px;
}

.xendit-dropdown-channel-logo {
  height: 24px;
  width: 32px;
  object-fit: contain;
  padding: 2px 0px;
  padding-right: 12px;
  border-right: 1px solid var(--xendit-color-border);
}

.xendit-dropdown-text {
  padding: 12px;
  padding-right: 0;
  white-space: nowrap;
  overflow: clip;
}

.xendit-dropdown-chevron {
  height: 38px;
  width: 38px;
  padding: 12px;
  box-sizing: border-box;
}

.xendit-dropdown:disabled {
  background: var(--xendit-color-disabled);
  cursor: default;
}

.xendit-dropdown-overlay {
  contain: style;
  position: absolute;
  z-index: var(--xendit-z-index-overlay);
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  padding: 4px;
  margin: 4px 0;
  box-sizing: border-box;
  background-color: var(--xendit-color-background);
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  list-style: none;
  box-shadow: var(--xendit-card-shadow);
  overflow-y: auto;
  overscroll-behavior: none;
}

.xendit-dropdown-overlay ul {
  list-style: none;
  padding: 4px;
  margin: 0;
}

.xendit-dropdown-search {
  position: sticky;
  top: 0;
  z-index: 1;
}

.xendit-dropdown-search input {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  box-sizing: border-box;
  outline: none;
}

.xendit-dropdown-search input:focus {
  border-color: var(--xendit-color-primary);
  box-shadow: var(--xendit-focus-shadow);
}

.xendit-dropdown-item {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 4px;
  border-radius: var(--xendit-radius-1);
  cursor: pointer;
  z-index: 0;
  --xendit-dropdown-item-hover-color: var(--xendit-color-background);
  --xendit-dropdown-item-hover-bg-color: var(--xendit-color-primary);
}

.xendit-dropdown-item-disabled {
  --xendit-dropdown-item-hover-color: var(--xendit-color-text);
  --xendit-dropdown-item-hover-bg-color: var(--xendit-color-disabled);
}

.xendit-dropdown-item-active {
  box-shadow: var(--xendit-focus-shadow);
}

.xendit-dropdown-item:hover {
  background-color: var(--xendit-dropdown-item-hover-bg-color);
}

.xendit-dropdown-item:hover .xendit-dropdown-item-text,
.xendit-dropdown-item:hover .xendit-dropdown-item-description {
  color: var(--xendit-dropdown-item-hover-color);
}

.xendit-dropdown-item-selected {
  color: var(--xendit-color-primary);
}

.xendit-dropdown-item:hover .xendit-dropdown-item-selected {
  color: var(--xendit-dropdown-item-hover-color);
}

.xendit-dropdown-item.xendit-dropdown-has-asset {
  grid-template-columns: auto 1fr auto;
}

.xendit-dropdown-item-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.xendit-dropdown-item-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.xendit-dropdown-item-disabled:hover,
.xendit-dropdown-item-disabled.xendit-dropdown-item-active {
  background-color: var(--xendit-color-disabled);
}

/* Dropdown ends */

/* Button */
.xendit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  font-family: var(--xendit-font-family);
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: -0.09px;
  cursor: pointer;
  transition: background-color var(--xendit-animation-duration)
    var(--xendit-animation-ease);
}
.xendit-button-primary-rounded {
  border: 1px solid transparent;
  background-color: var(--xendit-color-primary);
  color: var(--xendit-color-background);
  border-radius: var(--xendit-radius-1);
}
.xendit-button-white-rounded {
  background-color: var(--xendit-color-background);
  color: var(--xendit-color-text);
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
}
.xendit-button-white-rounded:hover {
  background-color: var(--xendit-color-border);
}
.xendit-button-block {
  width: 100%;
}
.xendit-button-sm {
  padding: 8px 12px;
  font-size: 12px;
  line-height: 16px;
}

@keyframes xendit-button-loading-spinner-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.xendit-button-loading-spinner {
  width: 16px;
  height: 16px;
  animation: xendit-button-loading-spinner-spin 1s linear infinite;
}

.xendit-skeleton-field button {
  height: 42px;
  background-color: var(--xendit-color-disabled);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--xendit-color-text-secondary);
}

/* Button ends */

/* Form Simulation Helper */
.xendit-form-simulation-root {
  position: relative;
}
.xendit-form-simulation-trigger {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}
.xendit-form-simulation-popover {
  position: absolute;
  top: 100%;
  right: 0;

  width: 350px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 4px;
  padding: 12px;
  border: 1px solid var(--xendit-color-border);
  background: color-mix(
    in srgb,
    var(--xendit-color-background),
    transparent 40%
  );
  backdrop-filter: blur(6px);
  box-shadow: var(--xendit-card-shadow);
}

.xendit-form-simulation-popover .xendit-dropdown-overlay {
  width: auto;
}

.xendit-form-simulation-scenario-icon {
  height: 28px;
  width: 40px;
  border-radius: 4px;
  border: 1px solid var(--xendit-color-border);
  object-fit: contain;
  background-color: white;
}

/* Form Simulation Helper ends */

.xendit-tooltip {
  position: absolute;
  top: 100%;
  right: 50%;
  transform: translateX(50%);
  background-color: black;
  color: white;
  padding: 8px 12px;
  border-radius: var(--xendit-radius-1);
  white-space: nowrap;
  z-index: 1000;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  animation: xendit-tooltip-fade-in var(--xendit-animation-duration)
    var(--xendit-animation-ease);
  opacity: 1;
}

@keyframes xendit-tooltip-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Instructions */

.xendit-instructions-bold {
  font-weight: 600;
}
.xendit-instructions-bullet-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 8px 0;
}
.xendit-instructions-bullet-list::marker {
  color: var(--xendit-color-text-secondary);
}
.xendit-instructions-form-card {
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--xendit-color-border);
  gap: 4px;
  width: 321px;
}
.xendit-instructions-form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--xendit-color-border);
}
.xendit-instructions-form-field-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--xendit-color-text-secondary);
}
.xendit-instructions-form-field-value {
  font-size: 14px;
  line-height: 20px;
  color: var(--xendit-color-text);
}
.xendit-instructions-form-heading {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 4px 0;
}
.xendit-instructions-italics {
  font-style: italic;
}
.xendit-instructions-numbered-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  list-style-type: decimal;
  padding-left: 40px;
}
.xendit-instructions-single-tab-heading {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin-bottom: 16px;
}
.xendit-instructions-step-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.xendit-instructions-step-li {
  font-size: 14px;
  line-height: 20px;
}
.xendit-instructions-step-li p {
  margin: 0;
}
.xendit-instructions-step-li::marker {
  margin-left: 20px;
}
.xendit-instructions-tab-button {
  font-family: var(--xendit-font-family);
  font-size: 14px;
  padding: 8px 4px;
  background-color: transparent;
  border: none;
  border-top: 3px solid transparent;
  border-bottom: 3px solid transparent;
  cursor: pointer;
}
.xendit-instructions-active-tab {
  border-bottom-color: var(--xendit-color-primary);
  color: var(--xendit-color-primary);
  font-weight: 600;
}
.xendit-instructions-tab-list {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--xendit-color-border);
}
.xendit-instructions-table {
  font-size: 14px;
  line-height: 16px;
  width: 100%;
  border-collapse: collapse;
}
.xendit-instructions-table-cell {
  padding: 4px;
  border: 1px solid black;
}
.xendit-instructions-table-header {
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  padding: 4px;
  border: 1px solid black;
}

/* Instructions ends */

.xendit-default-action-container {
  display: flex;
  flex-direction: column;
  border: none;
}

.xendit-action-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
}

.xendit-action-iframe {
  border: none;
  width: 100%;
  height: 100%;
}

.xendit-action-present-to-customer {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.xendit-action-present-to-customer-affirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* qr actions */
.xendit-action-qr-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 384px;
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  padding: 16px;
  overflow: hidden;
}
.xendit-qr-merchant-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.xendit-action-qr-channel-logo {
  height: 64px;
  align-self: center;
  object-fit: contain;
}
.xendit-action-qr-qrcode-container {
  width: 100%;
  position: relative;
  z-index: var(--xendit-z-index-overlay);
}

/* barcode actions */
.xendit-action-barcode-channel-logo {
  height: 64px;
  align-self: center;
  object-fit: contain;
}
.xendit-action-barcode-barcode-container {
  width: 100%;
  position: relative;
  z-index: var(--xendit-z-index-overlay);
  display: flex;
  justify-content: center;
}

/* va actions */
.xendit-action-va-content,
.xendit-action-barcode-content {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--xendit-color-border);
  border-radius: var(--xendit-radius-1);
  padding: 16px;
  padding-bottom: 24px;
}
.xendit-action-va-details,
.xendit-action-barcode-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.xendit-action-va-detail-item,
.xendit-action-barcode-detail-item {
  display: flex;
  align-items: center;
  gap: 12px;
}
.xendit-action-va-detail-content,
.xendit-action-barcode-detail-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.xendit-action-va-heading,
.xendit-action-barcode-heading {
  color: var(--xendit-color-text-placeholder);
}
.xendit-action-va-tag {
  color: var(--xendit-color-text-placeholder);
  background-color: #fafafa;
  padding: 2px 4px;
  border-radius: 4px;
}

/* redirect, deeplink, and push notification actions */
.xendit-redirect-instructions {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
}

.xendit-redirect-instructions-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 112px;
  height: 112px;
  border: 2px solid var(--xendit-color-border);
  border-radius: 50%;
  overflow: hidden;
  box-shadow: var(--xendit-card-shadow);
}

.xendit-redirect-instructions-logo img {
  width: 96px;
  height: 96px;
  object-fit: contain;
}

.xendit-redirect-instructions-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.xendit-redirect-instructions-button {
  display: inline-block;
  border-radius: 99px;
  color: white;
  padding: 12px 16px;
  background-color: var(--xendit-channel-brand-color, #000);
  text-decoration: none;
  white-space: nowrap;
  box-shadow: var(--xendit-card-shadow);
}

.xendit-redirect-instructions-button:hover,
.xendit-redirect-instructions-button:active {
  filter: brightness(1.1);
}

/* googlepay */

.gpay-card-info-container-fill {
  display: flex;
}
